import React, { useState, useEffect, useRef } from 'react';
// Import Google Maps components and Bootstrap Button
import { APIProvider, Map, Marker, InfoWindow } from '@vis.gl/react-google-maps';
import { Button } from 'react-bootstrap';
import ErrorAlert from '../components/Alerts/ErrorAlert';
import AlertSmall from '../components/Alerts/AlertSmall';

// Main Maps component
const Maps = ({
  modo, position, onClickCoordenada,
  tiendas, event, logo, zoom, width, height, address
}) => {
  // State for current position
  const [posicionActual, setPosicionActual] = useState(null);
  // State for address coordinates
  const [direccionCoords, setDireccionCoords] = useState(null);
  // State for points on the map
  const [puntos, setPuntos] = useState([]);
  // State for selected store
  const [selectedTienda, setSelectedTienda] = useState(null);
  // State for GPS error messages
  const [gpsError, setGpsError] = useState(null);
  // Default center for the map
  const [DEFAULT_CENTER, setDEFAULT_CENTER] = useState({ lat: 8.9824, lng: -79.5199 });
  // State for selection status
  const [selected, setSelected] = useState(false);

  // Map reference
  const mapRef = useRef(null);
  const realMapRef = useRef(null);

  // State for search input value
  const [searchValue, setSearchValue] = useState('');
  // State for search coordinates
  const [searchCoords, setSearchCoords] = useState(null);
  // Reference for the input field
  const inputRef = useRef(null);
  const autocompleteRef = useRef(null);

  // State for autocomplete suggestions
  const [suggestions, setSuggestions] = useState([]);
  // State to show/hide suggestions
  const [showSuggestions, setShowSuggestions] = useState(false);

  // Effect to fetch autocomplete suggestions from Google Places API
  useEffect(() => {
    if (!window.google || !window.google.maps || !window.google.maps.places) return;
    if (!searchValue) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }
    const service = new window.google.maps.places.AutocompleteService();
    const request = {
      input: searchValue,
      componentRestrictions: { country: 'pa' },
      location: posicionActual ? new window.google.maps.LatLng(posicionActual.lat, posicionActual.lng) : undefined,
      radius: posicionActual ? 50000 : undefined,
    };
    service.getPlacePredictions(request, (predictions, status) => {
      if (status === window.google.maps.places.PlacesServiceStatus.OK && predictions) {
        setSuggestions(predictions);
        setShowSuggestions(true);
      } else {
        setSuggestions([]);
        setShowSuggestions(false);
      }
    });
  }, [searchValue, posicionActual]);

  // Function to handle selection of a suggestion from the list
  const handleSuggestionClick = (suggestion) => {
    setSearchValue(suggestion.description);

    // Fetch place details from Google Places API
    const placesService = new window.google.maps.places.PlacesService(document.createElement('div'));
    placesService.getDetails({ placeId: suggestion.place_id }, (place, status) => {
      if (status === window.google.maps.places.PlacesServiceStatus.OK && place.geometry && place.geometry.location) {
        const coords = {
          lat: place.geometry.location.lat(),
          lng: place.geometry.location.lng()
        };
        setSearchCoords(coords);
        setPosicionActual(coords);
        setSelected(true);
        if (typeof onClickCoordenada === "function") {
          onClickCoordenada(coords);
          setDEFAULT_CENTER(coords);
          setShowSuggestions(false);
          setSuggestions([]);
          setSearchValue(" ");
        }
      }
    });
  };

  // Function to handle map load event
  const handleMapLoad = (mapInstance) => {
    mapRef.current = mapInstance; // Store the real Google Maps instance
  };

  // Function to get current geolocation and center the map
  const actiongeolocalizar = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const coords = {
            lat: position.coords.latitude,
            lng: position.coords.longitude
          };
          setDEFAULT_CENTER(coords)
          setPosicionActual(coords);
          setSelected(true);
          setGpsError(null);
          // Center the map on the obtained location
          if (mapRef.current) {
            mapRef.current.panTo(coords);
            mapRef.current.setZoom(zoom || 16);
          }
        },
        (error) => {
          if (error.code === error.PERMISSION_DENIED) {
            setGpsError("You must allow location for this page.");
            setTimeout(() => setGpsError(null), 4000);
          } else {
            setGpsError("Error getting location: " + error.message);
            setTimeout(() => setGpsError(null), 4000);
          }
        }
      );
    } else {
      AlertSmall("Error getting location", "error");
      setGpsError("Geolocation is not supported in this browser.");
      setTimeout(() => setGpsError(null), 4000);
    }
  }

  // Function to render store data in InfoWindow
  const viewDataSore = (store) => (
    <div className='col-12 col-md-12 p-2' onClick={() => event(store)}>
      <label className="card-restaurant">
        <span className="badge badge-success rounded-2 position-absolute badge-position">Open</span>
        <input name="plan" className="radio" type="radio" />
        <span className="plan-details">
          <div className="d-flex justify-content-start">
            <img src={logo}
              alt="Restaurant" className="rounded-2  border img-restaurant " />
          </div>
          <span className="plan-type" translate='no'>{store.branchofficeName}</span>
          <p className="plan-description" translate='no'>{store.businessName}</p>
          <div className="d-flex gap-2">
            <span className="badge bg-light text-secondary">Restaurant </span>
          </div>
        </span>
      </label>
    </div>
  );

  // Effect to set initial position based on mode
  useEffect(() => {
    if (modo === "geolocate" || modo === "puntear-geolocator") {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            setDEFAULT_CENTER({
              lat: position.coords.latitude,
              lng: position.coords.longitude
            })
            setPosicionActual({
              lat: position.coords.latitude,
              lng: position.coords.longitude
            });
            setSelected(true);
            setGpsError(null);
          },
          (error) => {
            console.log(error);
          }
        );
      } else {
        setGpsError("Geolocation is not supported in this browser.");
      }
    } else {
      setDEFAULT_CENTER({
        lat: parseFloat(address.lat),
        lng: parseFloat(address.lng),
      })
      setPosicionActual({
        lat: parseFloat(address.lat),
        lng: parseFloat(address.lng),
      })
    }
  }, [modo]); // Only once on mount

  // Effect to update position in "puntear-geolocator" mode
  useEffect(() => {
    if (modo === "puntear-geolocator") {
      if (posicionActual) {
        position(posicionActual);
        console.log("Current position geolocator:", posicionActual);
      }
    }
  }, [posicionActual, modo]);

  // Function to handle map click events
  const handleMapClick = (e) => {
    console.log(e);
    if (modo === "puntear-geolocator") {
      const coords = { lat: e.detail.latLng.lat, lng: e.detail.latLng.lng };
      setDEFAULT_CENTER(coords)
      setPosicionActual(coords);
      setSelected(true);
      if (typeof onClickCoordenada === "function") {
        onClickCoordenada(coords);
      }
    }
    if (modo === "puntear") {
      const coords = { lat: e.detail.latLng.lat, lng: e.detail.latLng.lng };
      setPuntos([coords]);
      setSelected(true);
      if (typeof onClickCoordenada === "function") {
        onClickCoordenada(coords);
      }
    }
  };
  const [showIdleMsg, setShowIdleMsg] = useState(false);

  // Function to render the map and its controls
  const maprender = (position) => {
    return (
      <APIProvider apiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY}
        libraries={['places']}>
        <div style={{ width: width, height: height, position: 'relative', borderRadius: 12, overflow: 'hidden' }}>

          {gpsError && (
            <div style={{
              position: 'absolute',
              top: 20,
              left: '50%',
              transform: 'translateX(-50%)',
              background: '#fff3cd',
              color: '#856404',
              padding: '12px 24px',
              borderRadius: 8,
              boxShadow: '0 2px 8px rgba(0,0,0,0.10)',
              zIndex: 1000
            }}>
              <b>Location permission required:</b><br />
              {gpsError}
            </div>
          )}

          <Map
            key={posicionActual ? `${posicionActual.lat},${posicionActual.lng}` : 'default'}
            ref={mapRef}
            style={{ width: '100%', height: '100%' }}
            defaultCenter={position}
            defaultZoom={zoom}
            onIdle={() => {
              if (modo == "puntear-geolocator") {
                console.log(selected, "Selected status onIdle", posicionActual);
                if (!selected) {
                  setPosicionActual(null);
                  onClickCoordenada({ lat: 0, lng: 0 });
                }
                setSelected(false);
                // Reset for next movement
              }
            }}
            onClick={handleMapClick}
            onLoad={handleMapLoad}
            mapTypeControl={modo !== "location"}
            streetViewControl={modo !== "location"}
            rotateControl={modo !== "location"}
            fullscreenControl={modo !== "location"}
            zoomControl={true}
          >
            {/* Marker for geolocate or puntear-geolocator mode */}
            {modo === "geolocate" || modo == "puntear-geolocator" ? (
              position && (
                <>
                  <div
                    style={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -100%)',
                      zIndex: 1001,
                      pointerEvents: 'none',
                      width: 70,
                      height: 70,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      background: '#ffffffff',
                      borderRadius: '50% 50% 50% 0',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                      border: posicionActual ? '2px solid #1976D2' : '2px solid #1976d26b',
                      opacity: posicionActual ? 1 : 0.5 // translucent if not selected
                    }}
                  >
                    <img
                      src={logo}
                      alt="Pointer"
                      style={{
                        width: 50,
                        height: 50,
                        objectFit: 'contain',
                        filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.10))'
                      }}
                    />
                  </div>
                </>
              )
            ) : null}

            {/* Markers and InfoWindows for store list mode */}
            {modo === "liststore" ? (
              <>
                {tiendas.map(tienda => (
                  <Marker
                    key={tienda.id}
                    position={{ lat: parseFloat(tienda.lat), lng: parseFloat(tienda.lng) }}
                    title={tienda.nombre}
                    onClick={() => setSelectedTienda(tienda)}
                  />
                ))}
                {selectedTienda && (
                  <InfoWindow
                    position={{ lat: parseFloat(selectedTienda.lat), lng: parseFloat(selectedTienda.lng) }}
                    onCloseClick={() => setSelectedTienda(null)}
                  >
                    {viewDataSore(selectedTienda)}
                  </InfoWindow>
                )}
              </>
            ) : null}

            {/* Markers for points in puntear or puntear-geolocator mode */}
            {modo === "puntear" || modo == "puntear-geolocator" && puntos.map((p, idx) => (
              <Marker key={idx} position={p} title={`Point ${idx + 1}`} />
            ))}

            {/* Marker for selected location */}
            {modo === "location" && address && address.lat && address.lng && (
              <Marker
                position={{ lat: parseFloat(address.lat), lng: parseFloat(address.lng) }}
                title="Selected location"
                icon={{
                  url: logo,
                  scaledSize: { width: 70, height: 70 },
                  anchor: { x: 35, y: 60 }
                }}
              />
            )}
          </Map>

          {/* Additional controls can be added here */}
          <div style={{
            position: 'absolute',
            top: 10,
            left: 10,
            background: '#fff',
            padding: 10,
            borderRadius: 8,
            boxShadow: '0 2px 8px rgba(0,0,0,0.10)'
          }}>
            {/* Add controls here if needed */}
          </div>
        </div>
      </APIProvider>
    );
  }

  // Main render
  return (<>
    {/* Geolocate button */}
    {/*<button
      type='button'
      className='btn btn-primary mb-2'
      style={{ position: 'absolute', top: 10, right: 10, zIndex: 1001 }}
      onClick={() => actiongeolocalizar()}
    >
      Geolocate
    </button>*/}

    {modo === "puntear" || modo == "puntear-geolocator" && 
    <div className='mb-3 d-flex justify-content-center align-items-center'>
      <div style={{
        width: '100%',
        background: '#fffefeff',
        borderRadius: 8,
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.35)',
        padding: 8,
        position: 'relative'
      }}>
        {/* Input and search icon in a row */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          width: '100%'
        }}>
          <input
            ref={inputRef}
            type="text"
            value={searchValue}
            onChange={e => setSearchValue(e.target.value)}
            placeholder="Burcar dirección..."
            style={{
              width: '100%',
              border: 'none',
              outline: 'none',
              fontSize: 16,
              background: 'transparent'
            }}
          />
          <span style={{ marginLeft: 8, color: '#1976D2', fontSize: 20 }}>
            <i className="uil uil-search"></i>
          </span>
        </div>
        {/* Suggestions below the input */}
        {showSuggestions == true ? <>
          {suggestions.length > 0 && (
            <ul style={{
              position: 'absolute',
              top: 40,
              left: 0,
              right: 0,
              background: '#fff',
              borderRadius: 8,
              boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
              zIndex: 1200,
              listStyle: 'none',
              margin: 0,
              padding: 0,
              maxHeight: 200,
              overflowY: 'auto'
            }}>
              {suggestions.map(suggestion => (
                <li
                  key={suggestion.place_id}
                  style={{
                    padding: '8px 12px',
                    cursor: 'pointer',
                    borderBottom: '1px solid #eee'
                  }}
                  onMouseDown={() => handleSuggestionClick(suggestion)}
                >
                  {suggestion.description}
                </li>
              ))}
            </ul>
          )}</> : <></>}
      </div>
    </div>}

    {/* Render the map with current or default center */}
    {posicionActual !== null ? maprender(posicionActual) : maprender(DEFAULT_CENTER)}
    <div className=' d-flex justify-content-center'>
      {modo === "puntear" || modo == "puntear-geolocator" ? <>
        <button
          onClick={() => (actiongeolocalizar())}
          className="btn btn-outline-primary bg-primary-subtle mt-2 border-0 text-light-emphasis"
        ><i class="uil uil-location-pin-alt"></i> Current location
        </button></> : <></>}
    </div>
    {/* GPS permission required message */}
    {/*</> : <>
      {maprender(DEFAULT_CENTER)}
      <div className='col-12 col-md-12  p-2  justify-content-center align-items-center' >
        <label className="border rounded-4 w-100">
          <span className="">
            <div className="">
              <div className='p-2 '>
                <div className="d-flex justify-content-center align-items-center"><i class="uil uil-map"></i> </div>
                <div className="d-flex justify-content-center align-items-center">
                  <h2 class=" text-center fs-6 text-theme ">GPS permission required</h2>
                </div>
              </div>
            </div>
          </span>
        </label>
      </div ></>*/}
  </>);
};

export default Maps;

// Utility function to get current coordinates using geolocation
export function getCurrentCoords() {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error("Geolocation is not supported in this browser."));
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (position) => {
        resolve({
          lat: position.coords.latitude,
          lng: position.coords.longitude
        });
      },
      (error) => {
        reject(error);
      }
    );
  });
}